import React, { Component } from 'react'
import { Dimmer, Loader, Transition, Message } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import TinyMce from 'react-tinymce'
import _ from 'lodash'
import TrillDescription from 'trill-description'
import { OembedApi } from 'trill-api-admin-client'
import { v4 as uuidv4 } from 'uuid'

import HtmlEmbedDialog, {
  parseEmbedFigure,
  safeSandbox,
  resolveEmbedDimensions,
  stripEmbedScripts,
} from './HtmlEmbedDialog'
import { highlightWords } from '../utils/HighlightWord'

import LogLevel from '../LogLevel'

import {
  YOUTUBE_IFRAME_EMBED_PATTERN,
  FACEBOOK_IFRAME_EMBED_PATTERN,
  BLOCKQUOTE_EMBED_SNS_CODE_PATTERN,
  SCRIPT_EMBED_SNS_CODE_PATTERN,
  escapeEmbedCode,
} from '../constants/EmbedSNSPattern'
import {
  DEVICE_DEPENDENT_CHARS,
  SPECIAL_SPACES,
  HTML_ENTITY_SPACE_PATTERNS,
} from '../constants/TextSanitizationConstants'
import { TOAST_TYPES, TOAST_ICONS } from '../enums/Toast'

import './ArticleDescriptionEditor.css'

const logger = LogLevel.getLogger('ArticleDescriptionEditor')
const oembedApi = new OembedApi()

const HTML_EMBED_ALLOW_LIST = new TrillDescription.TrillHtmlIframeEmbed.AllowList()
const HTML_EMBED_RESIZE_MAX_PX = 4000
const HTML_EMBED_AUTO_RESIZE_SCRIPT = `
(function () {
  function post(target, h) {
    try { target.postMessage({ type: 'trill:resize', height: h }, '*'); } catch (e) {}
  }
  function sendSize() {
    var h = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      document.body.offsetHeight
    );
    if (h <= 0) return;
    post(parent, h);
    if (top !== parent) post(top, h);
  }
  window.addEventListener('load', sendSize);
  if (window.ResizeObserver) {
    new ResizeObserver(sendSize).observe(document.body);
  }
})();
`

const warningByteLength = 23068672 // 22MB
const imageCaptionTextDefault = '出典元の情報、または画像の説明'
const emptyEditorContentErrorMessage = '本文を入力してください。'
const externalImageUrlRegexString = `<img([^>]+)src="(?!(data:|blob:|${process.env.REACT_APP_TRILL_CLOUDFRONT_IMAGE_URL}))([^"]+)"([^>]+)>`
const externalImageUrlErrorMessage =
  '画像URLの内容に誤りがあります。編集画面の本文内を右クリックし、画像を挿入からアップロードし直してください。'
const allowedImageTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'image/heic',
  'image/avif',
]

const propTypes = {
  /**
   * 記事 ID（oEmbed の認証に使う）
   */
  articleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * 記事本文
   */
  description: PropTypes.string,

  /**
   * 初期処理完了後に呼び出す外部関数
   *
   * @param {Object} data - 全ての props と、その他の変更に関連するデータ
   * @param {Object} data.editor - 記事本文エディター（TinyMCE）のインスタンス
   */
  onInit: PropTypes.func,

  /**
   * 概要を変更した時に呼び出す外部関数
   *
   * @param {Object} data - 全ての props と、その他の変更に関連するデータ
   * @param {string} data.description - 変更された記事本文
   */
  onChange: PropTypes.func,

  /**
   * 概要を変更した時に呼び出す外部関数
   *
   * @param {Object} data - 全ての props と、その他の変更に関連するデータ
   * @param {Blob} data.blob - 記事本文に挿入した画像データ
   * @param {function} data.success - アップロードの成功時にコールする関数
   * @param {function} data.failure - アップロードの失敗時にコールする関数
   */
  onImageUpload: PropTypes.func,
}

const defaultProps = {
  description: '',
  isTinyMceScriptLoaded: false,
}

const defaultToast = {
  isShow: false,
  type: null,
  icon: null,
  message: null,
}

class ArticleDescriptionEditor extends Component {
  state = {
    isBusy: false,
    toast: defaultToast,
    htmlEmbedDialog: { open: false, initial: {}, targetNode: null },
  }

  /**
   * 記事本文エディター（TinyMCE）の初期化オプション
   */
  config = {
    table_default_attributes: {
      class: 'trill-article-table',
      border: '1',
    },

    language_url: '/scripts/tinymce/langs/ja.js',

    inline: true,
    theme: 'inlite',

    plugins: ['link image media lists hr', 'autolink imagetools paste', 'contextmenu preview visualblocks code table'],

    // This option allows you to disable TinyMCE's default paste filters when set to false.
    paste_enable_default_filters: false,

    // SNS 埋め込みで iframe に script を埋め込むため true に設定
    allow_script_urls: true,

    /**
     * Allow <span> tags with style attributes, which are not permitted by TinyMCE's default valid_elements.
     * Also allow <script> and <iframe> tags to support social media, TRILL html iframe embeds.
     */
    extended_valid_elements:
      'span[!*],script[async|defer|src|type],iframe[data-mce-bogus|src|srcdoc|sandbox|width|height|loading|style|frameborder|allow|allowfullscreen|scrolling|title|referrerpolicy]',

    image_caption: false,
    image_dimensions: false,
    paste_data_images: true,

    media_alt_source: false,
    media_poster: false,

    // FIXME: quicklink のツールチップが日本語化されてない
    selection_toolbar: [
      'quicklink',
      'linkButton linkButtonAccent',
      '|',
      'bold italic',
      '|',
      'h2 h3 h4',
      '|',
      'blockquote cite',
      '|',
      'bullist numlist',
      '|',
      'snsEmbedX',
      'snsEmbedTikTok',
      'snsEmbedReddit',
      '|',
      'checkDeviceChars',
    ].join(' '),

    insert_toolbar:
      'quickimage media embedSNS | blockquote | bullist numlist | hr | articleInternalLink | table | htmlIframeEmbed',

    contextmenu: 'visualblocks pastetext code cell column row downloadimage',
    contextmenu_never_use_native: true,

    formats: {
      h1: { block: 'h1', classes: 'heading' },
      h2: { block: 'h2', classes: 'heading' },
      h3: { block: 'h3', classes: 'heading' },
      h4: { block: 'h4', classes: 'heading' },
      h5: { block: 'h5', classes: 'heading' },
      h6: { block: 'h6', classes: 'heading' },
      // FIXME: 引用の中にリストも入れられた方が良い？(新デザイン仕様では定義されてないが)
      // TODO: リストの中に引用が入るのは防ぎたい
      blockquote: { block: 'blockquote' },
      cite: { inline: 'cite' },
      blockButton: { selector: 'a', classes: 'button button--block' },
      embed: { block: 'p' },
    },

    content_css: [
      // TRILL 記事本文の表示に必要なスタイル
      process.env.REACT_APP_TRILL_DESCRIPTION_STYLE_URL,
    ],
  }

  /**
   * 記事本文エディター（TinyMCE）のインスタンス
   */
  editor = null

  /**
   * 記事本文エディターの HTML 出力に必要な記事のタイトル
   */
  title = ''

  /**
   * 記事本文エディターの HTML 出力に必要な記事の概要
   */
  summary = ''

  /**
   * 記事本文
   */
  description = ''

  /**
   * 記事本文エディターで（１回でも）コンテンツの編集が行われたかどうか
   */
  isDirty = false

  /**
   * 記事本文エディターからフォーカスが（１回でも）外れた事があるかどうか
   */
  isBlurred = false

  /**
   * 容量警告を表示しているかどうか
   */
  isWarningByteLength = false

  /**
   * 記事本文に対するエラー
   */
  editorError = null

  constructor(props) {
    super(props)

    this.description = this.props.description

    // 記事本文の初期値 (記事本文変更判定で使用)
    this.initialDescription = this.props.description
    // 記事本文に変更があったかどうか
    this.descriptionModified = false

    this.config.setup = editor => {
      editor.on('Dirty', () => {
        logger.debug('TinyMCE events (Dirty)')
        this.isDirty = true
      })

      editor.on('Blur', () => {
        logger.debug('TinyMCE events (Blur)')
        this.isBlurred = true
        const editorContent = editor.getContent()
        // フォーカスが外れたときに記事本文情報を通知
        this.handleChange(editorContent, this.editorError)
      })

      editor.on('Change Blur KeyUp', () => {
        logger.debug('TinyMCE events (Change/Blur/KeyUp)')

        const editorContent = editor.getContent()
        // Exclude sandboxed iframe content from external image URL validation.
        const contentForExternalImageCheck = stripEmbedScripts(editorContent)
        const isExternalImageUrl =
          contentForExternalImageCheck &&
          contentForExternalImageCheck.match(new RegExp(externalImageUrlRegexString, 'gmi'))
        const editorErrorMessage = _.get(this.editorError, 'message', null)

        // すでに本文エディターで編集を行った後なら、本文が必ず入力される様にエラーメッセージを表示
        if (this.isDirty && this.isBlurred) {
          if (!_.isEqual(editorErrorMessage, emptyEditorContentErrorMessage) && !editorContent) {
            // editorError が null かつ 本文が空の場合 = エラー文言を表示する
            this.editorError = new Error(emptyEditorContentErrorMessage)
            this.handleChange(editorContent, this.editorError)
          } else if (!_.isEqual(editorErrorMessage, externalImageUrlErrorMessage) && isExternalImageUrl) {
            this.editorError = new Error(externalImageUrlErrorMessage)
            this.handleChange(editorContent, this.editorError)
          } else if (editorErrorMessage && editorContent && !isExternalImageUrl) {
            // editorError が存在する (エラー文言を表示している) かつ 本文が存在する場合 = エラー文言を非表示にする
            this.editorError = null
            this.handleChange(editorContent, this.editorError)
          }
        } else if (this.isDirty && !_.isEqual(this.initialDescription, this.description) && !this.descriptionModified) {
          // 記事本文の初期値から変更があった場合 かつ this.descriptionModified が false のときのみ通知を行う
          this.descriptionModified = true
          this.handleChange(editorContent, this.editorError)
        } else if (this.isDirty && _.isEqual(this.initialDescription, this.description) && this.descriptionModified) {
          // 記事本文の初期値から変更してから初期値に戻った場合 かつ this.descriptionModified が true のときのみ通知を行う
          this.descriptionModified = false
          this.handleChange(editorContent, this.editorError)
        }
      })

      const handleCheckImageSize = image => {
        if (_.isNil(image)) {
          return
        }

        const imageLoadHandler = () => {
          // 画像の表示サイズが指定されてない場合は、画像の元の大きさで指定
          if (_.isNil(image.getAttribute('width')) || _.isNil(image.getAttribute('height'))) {
            const imageNodeCheckSizeId = image.getAttribute('data-check-size-id')
            const currentImage =
              document.querySelector(`.ArticleDescriptionEditor img[data-check-size-id="${imageNodeCheckSizeId}"]`) ||
              document.querySelector(`.ArticleDescriptionEditor img[src*="${image.src}"]`)

            if (!_.isNil(currentImage)) {
              currentImage.setAttribute('width', image.naturalWidth)
              currentImage.setAttribute('height', image.naturalHeight)
              currentImage.setAttribute('style', `aspect-ratio: ${image.naturalWidth}/${image.naturalHeight}`)
              currentImage.removeAttribute('data-check-size-id')
            }
          }

          // 画像のサイズが小さい時は警告メッセージを表示
          if (image.naturalWidth < 640) {
            editor.notificationManager.open({
              text: '画像の横幅のサイズは推奨 2304 px 以上、最低 640 px 以上にしてください。',
              type: 'warning',
            })
          }

          image.removeEventListener('load', imageLoadHandler)
        }

        // サイズチェックは一度のみ行う
        if (
          _.isNil(image.getAttribute('data-size-checked')) ||
          _.isNil(image.getAttribute('width')) ||
          _.isNil(image.getAttribute('height'))
        ) {
          const imageNodeCheckSizeId = uuidv4()

          image.setAttribute('data-check-size-id', imageNodeCheckSizeId)
          image.addEventListener('load', imageLoadHandler)
          image.setAttribute('data-size-checked', true)
        }
      }

      editor.on('Paste', event => {
        const clipboard = (event.originalEvent || event).clipboardData
        if (!clipboard) {
          return
        }

        const imageFiles = _.compact(
          _.map(clipboard.items, item =>
            item.kind === 'file' && allowedImageTypes.includes(item.type) ? item.getAsFile() : null,
          ),
        )

        if (!_.isEmpty(imageFiles)) {
          event.preventDefault()

          const fileToHtml = file =>
            new Promise(resolve => {
              const reader = new FileReader()

              reader.onload = evt => {
                const img = new window.Image()
                const imgSrc = evt.target.result

                img.onload = () => {
                  const width = img.naturalWidth
                  const height = img.naturalHeight
                  const html = `
                    <figure class="image" contenteditable="false">
                      <img src="${imgSrc}" width="${width}" height="${height}" style="aspect-ratio: ${width}/${height}" data-size-checked="true" />
                      <figcaption contenteditable='true'>${imageCaptionTextDefault}</figcaption>
                    </figure>
                  `
                  resolve(html.trim())
                }
                img.src = imgSrc
              }
              reader.readAsDataURL(file)
            })

          Promise.all(imageFiles.map(fileToHtml)).then(htmlArray => {
            editor.insertContent(htmlArray.join(''))
          })
        }
      })

      editor.on('PastePreProcess', event => {
        let newContent = event.content

        /**
         * The span tag will be removed when content is added to the editor.
         * So we will need to handle to retain bold and italic properties.
         */
        const handleConvertSpanStyleToValidElement = () => {
          newContent = newContent.replace(/<span([^>]*)style="([^"]*)">([^<]+)<\/span>/gim, (match, p1, p2) => {
            let newValue = match

            if (p2.match(/font-style:italic/g)) {
              newValue = `<em>${newValue}</em>`
            }

            if (p2.match(/font-weight:(700|bold)/g)) {
              newValue = `<strong>${newValue}</strong>`
            }

            return newValue
          })
        }

        /**
         * When pasting, the image tag can be inside the tag of p, ul, ol, bold, italic,...
         *  - When the image is in a p tag (it contains only an image) or a figure, the parsing is fine.
         *  - But when it is in other tags like ul, ol, bold, italic or in a p tag containing text, the parse will lose the image or fail.
         * => Therefore, we will need to separate the image tag into a new block, inside p tag
         */
        const handleConvertSpanImageToBlockElement = () => {
          newContent = newContent.replace(
            /<span([^>]*)>(.*?)<img(.*?)src="(.*?)"([^>]*)>(.*?)([^<]+)<\/span>/gim,
            match => {
              return `<p>${match}</p>`
            },
          )
        }

        handleConvertSpanStyleToValidElement()
        handleConvertSpanImageToBlockElement()

        event.content = newContent
      })

      editor.on('PastePostProcess', event => {
        const handleChangeNodeName = (oldNode, newNodeName, newNodeInnerHtml, attributes = {}) => {
          const parentNode = oldNode.parentNode
          const newNode = document.createElement(newNodeName)

          if (!_.isNil(attributes)) {
            _.keys(attributes).forEach(attributeKey => newNode.setAttribute(attributeKey, attributes[attributeKey]))
          }

          newNode.innerHTML = newNodeInnerHtml
          parentNode.insertBefore(newNode, oldNode)
          parentNode.removeChild(oldNode)

          return newNode
        }

        const createImageObjectURL = async imageNode => {
          const imageNodeObjectUrlId = uuidv4()
          imageNode.setAttribute('data-object-url-id', imageNodeObjectUrlId)

          try {
            const blob = await fetch(imageNode.getAttribute('src')).then(r => r.blob())
            const objectUrl = URL.createObjectURL(blob)
            const currentImage =
              document.querySelector(`.ArticleDescriptionEditor img[data-object-url-id="${imageNodeObjectUrlId}"]`) ||
              document.querySelector(`.ArticleDescriptionEditor img[src*="${imageNode.src}"]`)

            if (currentImage) {
              currentImage.setAttribute('src', objectUrl)
              currentImage.setAttribute('data-mce-src', objectUrl)
              currentImage.removeAttribute('data-object-url-id')
            }
          } catch (error) {
            logger.error('create image object URL error', error)
          }
        }

        /**
         * Reformat the node, being like the one supported by Trill
         */
        const handleReformatNode = node => {
          if (!node) {
            return
          }

          /**
           * Remove attributes not held by Trill.
           * If you intentionally keep it, it will also be deleted when content is saved
           */
          _.each(['id', 'class', 'dir', 'aria-level', 'style', 'align'], attribute => {
            if (node.hasAttribute(attribute)) {
              node.removeAttribute(attribute)
            }
          })

          const nodeName = node.nodeName
          const parentNode = node.parentNode

          /**
           * Reformat the children of node
           */
          const handleReformatNodeChildren = () => {
            _.each(node.children, nodeChildren => {
              handleReformatNode(nodeChildren)
            })
          }

          /**
           * Remove redundant blank p tag (Keep image tag format)
           */
          const handleRemovePNodeChildrenRedundant = () => {
            node.innerHTML = node.innerHTML
              .replace(/<p([^>]*)>(.*?)<\/p>/gm, (match, p1, p2) => p2)
              .replace(/<img(.*?)src="(.*?)"([^>]*)>/gim, match => {
                return `<figure class="image" contenteditable="false">${match}<figcaption contenteditable="true">${imageCaptionTextDefault}</figcaption></figure>`
              })
          }

          /**
           * Set the figure attributes just like when creating a new figure to match the figure format that Trill supports
           */
          const handleSetTrillFigureAttributes = () => {
            node.className = 'image'
            node.setAttribute('contenteditable', false)
          }

          /**
           * Set the table attributes just like when creating a new table to match the table format that Trill supports
           */
          const handleSetTrillTableAttributes = () => {
            node.className = 'trill-article-table'
            node.border = '1'
            node.style.borderCollapse = 'collapse'
            node.style.width = '100%'
          }

          /**
           * Set the table cells attributes just like when creating a new table
           */
          const handleSetTrillTableCellsAttributes = () => {
            node.style.width = `${100 / parentNode.children.length}%`
          }

          if (nodeName.match(/^h[1-6]$/gi)) {
            /**
             * For heading tags, Trill only supports text content
             * Therefore, we just need to set the content as text
             */
            node.innerHTML = node.innerText
          } else if (nodeName.match(/^p$/gi)) {
            handleReformatNodeChildren()
          } else if (nodeName.match(/^a$/gi)) {
            handleReformatNodeChildren()
          } else if (nodeName.match(/^strong|em$/gi)) {
            handleReformatNodeChildren()

            /**
             * To eliminate the case where elements are nested and have no content
             */
            if (!node.innerText.trim()) {
              node.innerHTML = '<br />'
            }
          } else if (nodeName.match(/^br|hr$/gi)) {
            /**
             * For the break line tag, we will remove all unnecessary attributes, just leave the content blank
             */
            node.innerHTML = ''
          } else if (nodeName.match(/^ol|ul$/gi)) {
            handleReformatNodeChildren()
          } else if (nodeName.match(/^li$/gi)) {
            _.each(node.children, nodeChildren => {
              handleReformatNode(nodeChildren)

              /**
               * Trill only supports line break, bold, italic, and links in li tags.
               * Therefore, for the other tags we will need to remove them.
               */
              if (!nodeChildren.nodeName.match(/^br|b|strong|i|em|a$/gi)) {
                nodeChildren.outerHTML = nodeChildren.innerHTML
              }
            })
          } else if (nodeName.match(/^figure$/gi)) {
            handleReformatNodeChildren()
            handleSetTrillFigureAttributes()
          } else if (nodeName.match(/^img$/gi)) {
            /**
             * Create object url from original url
             */
            const imageUrl = node.getAttribute('src')
            if (imageUrl && !imageUrl.match(/^(blob|data):/gi)) {
              createImageObjectURL(node)
            }

            // Check image size
            handleCheckImageSize(node)

            /**
             * Separate the image tag into a new block, inside figure tag
             */
            if (!(parentNode.nodeName.match(/^figure$/gi) && parentNode.childNodes.length === 2)) {
              node = handleChangeNodeName(
                node,
                'figure',
                `${node.outerHTML}<figcaption contenteditable="true">${imageCaptionTextDefault}</figcaption>`,
                { class: 'image', contenteditable: false },
              )
            }
          } else if (nodeName.match(/^figcaption$/gi)) {
            handleReformatNodeChildren()
          } else if (nodeName.match(/^table$/gi)) {
            handleReformatNodeChildren()
            handleSetTrillTableAttributes()
          } else if (nodeName.match(/^colgroup|col$/gi)) {
            handleReformatNodeChildren()
          } else if (nodeName.match(/^thead|tbody|tfoot|tr$/gi)) {
            handleReformatNodeChildren()
          } else if (nodeName.match(/^th|td$/gi)) {
            handleReformatNodeChildren()
            handleSetTrillTableCellsAttributes()
          } else {
            handleReformatNodeChildren()

            /**
             * For other, which Trill unsupported tags, we will convert to p tag.
             * The convert can result in empty tags, so we'll need an extra step to remove these unnecessary, redundant tags.
             */
            node = handleChangeNodeName(node, 'p', node.innerHTML)
            handleRemovePNodeChildrenRedundant()
          }
        }

        const currentNode = event.node
        const childrenNodes = currentNode.children

        /**
         *
         * To ensure consistency when inserting multiple consecutive images
         * If the pasted content contains only one image, we will wrap that image in a <p> tag, without reformatting it into a <figure> tag.
         *
         */
        const childImage = _.find(childrenNodes, child => _.isEqual(child.nodeName.toLowerCase(), 'img'))
        if (_.isEqual(childrenNodes.length, 1) && !_.isNil(childImage)) {
          currentNode.innerHTML = `<p>${currentNode.innerHTML}</p>`
          return
        }

        /**
         * Reformat the node, before they are added
         */
        _.each(childrenNodes, nodeChildren => {
          handleReformatNode(nodeChildren)
        })

        /**
         * If the last element is an image, we break the line with a new line
         *
         * const childrenNodeSize = childrenNodes.length
         * if (childrenNodeSize && childrenNodes[childrenNodeSize - 1].nodeName.match(/^figure$/gi)) {
         *   event.node.innerHTML = `${event.node.innerHTML}<p></p>`
         * }
         */
      })

      // NodeChange をハンドルする
      editor.on('NodeChange', event => {
        if (this.isDirty) {
          logger.debug('TinyMCE events (NodeChange) - editor content props handle')

          const editorContent = editor.getContent()

          const byteLength = Buffer.byteLength(editorContent)
          this.handleByteLengthWarning(byteLength > warningByteLength)
        }
      })

      // TinyMCE のコンテンツ選択時のツールバーが、編集できない要素を選択しても表示される不具合の対応
      // TODO: TinyMCE のコアな部分を修正して本家の GitHub にプルリクあげる
      editor.on('NodeChange', event => {
        const selectionToolbar = document.querySelector('.mce-tinymce-inline.mce-floatpanel')

        if (!_.isNil(selectionToolbar)) {
          logger.debug('TinyMCE events (NodeChange) - selection toolbar', event.element, event.parents)

          // Setup Selection Toolbar
          if (
            event.element.getAttribute('contenteditable') === 'false' ||
            event.element.classList.contains('mce-content-body')
          ) {
            selectionToolbar.dataset.trillMceSelectionToolbarDisabled = true
          } else {
            selectionToolbar.dataset.trillMceSelectionToolbarDisabled = false
          }

          // Setup Edit Image Toolbar
          const editImageToolbarButton = selectionToolbar.querySelector(
            '.mce-toolbar .mce-btn-group .mce-btn[role="button"][aria-label="Edit image"]',
          )

          if (editImageToolbarButton && editImageToolbarButton.getAttribute('is-setup') !== 'true') {
            editImageToolbarButton.addEventListener('click', () => {
              const downloadImage = url => {
                const filename = url.split('/').pop()
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', filename)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }
              let numOfInterval = 0
              const intervalID = setInterval(() => {
                numOfInterval += 1

                const editImagePanels = document.querySelectorAll(
                  '.mce-container.mce-panel.mce-window[aria-label="Edit image"]',
                )
                const editImagePanel = editImagePanels[editImagePanels.length - 1]

                if (editImagePanel && editImagePanel.getAttribute('is-setup') !== 'true') {
                  const saveButton = editImagePanel.querySelector(
                    '.mce-container.mce-panel.mce-foot .mce-container-body .mce-widget.mce-btn[role=button]',
                  )
                  saveButton.querySelector('button .mce-txt').innerText = '記事反映'
                  saveButton.style.width = '72px'
                  saveButton.style.left = `${saveButton.offsetLeft - 24}px`

                  const downloadButton = document.createElement('div')
                  downloadButton.style.width = '94px'
                  downloadButton.style.height = '28px'
                  downloadButton.style.top = '10px'
                  downloadButton.style.left = `${saveButton.offsetLeft - 102}px`
                  downloadButton.className = 'mce-btn mce-primary mce-abs-layout-item mce-btn-has-text'
                  downloadButton.innerHTML = `
                    <button role="presentation" type="button" tabindex="-1" style="height: 100%; width: 100%; padding: 0;">
                      <span class="mce-txt">
                        ダウンロード
                      </span>
                    </button>
                  `
                  downloadButton.addEventListener('click', () => {
                    const imageUrl = editImagePanel
                      .querySelector('.mce-container-body.mce-window-body .mce-imagepanel img')
                      .getAttribute('src')

                    downloadImage(imageUrl)
                  })

                  saveButton.insertAdjacentElement('afterend', downloadButton)
                  editImagePanel.setAttribute('is-setup', 'true')
                  clearInterval(intervalID)
                }

                if (numOfInterval > 100) {
                  clearInterval(intervalID)
                }
              }, 200)
            })
            editImageToolbarButton.setAttribute('is-setup', 'true')
          }
        }
      })

      /**
       * --------------------------------------------------------------------
       * This logic has been implemented in function handleReformatNode above
       * --------------------------------------------------------------------
       * editor.on('PastePostProcess', data => {
       *   const currentNode = data.node
       *   const childImage = _.find(currentNode.children, child => _.isEqual(child.nodeName.toLowerCase(), 'img'))
       *
       *   if (_.isNil(childImage)) {
       *     return
       *   }
       *
       *   currentNode.innerHTML = `<p>${currentNode.innerHTML}</p>`
       * })
       *  */

      // Control image content based on TRILL specifications
      editor.on('NodeChange', event => {
        const currentNode = event.element
        /**
         * TRILL HTML Iframe Embed
         */
        if (
          _.isEqual(currentNode.nodeName.toLowerCase(), 'figure') &&
          currentNode.classList.contains('trill-html-iframe-embed')
        ) {
          logger.debug('TinyMCE events (NodeChange) - iframe embed', event.parents, currentNode)
          currentNode.setAttribute('contenteditable', false)
          return
        }

        /**
         *
         * FIGURE
         *
         */
        const parentFigure = this.findParentNode(currentNode, 'figure')
        logger.debug('TinyMCE events (NodeChange) - figure', event.parents, currentNode, parentFigure)
        if (_.isEqual(currentNode.nodeName.toLowerCase(), 'figure')) {
          const figureNode = this.normalizeFigureNode(currentNode)
          if (_.isNil(figureNode)) {
            return
          }

          // Set to false because keeping true prevents focus from leaving <figure> when selected
          figureNode.setAttribute('contenteditable', false)
        } else if (!_.isNil(parentFigure)) {
          const figureNode = this.normalizeFigureNode(parentFigure)
          if (_.isNil(figureNode)) {
            return
          }

          // Set to true because toolbar doesn't appear when selecting img or figcaption
          figureNode.setAttribute('contenteditable', true)
        }

        const childImage = this.findChildNode(currentNode, 'img')
        const childFigcaption = this.findChildNode(currentNode, 'figcaption')
        if (_.isNil(childImage)) {
          // Do nothing if <img> tag does not exist
          return
        }

        /**
         *
         * IMAGE
         *
         */
        logger.debug('TinyMCE events (NodeChange) - figure image', event.parents, currentNode, childImage)

        // When uploading an image in a ul, ol, blockquote,... tags, the image will be added to a new tag after that tag.
        // Because if the image is in these tags, you will lose it when TrillDescription.parse.
        // So please exit so you don't lose it.
        const currentLastParentNode = event.parents[event.parents.length - 1]
        if (!_.isNil(currentLastParentNode) && !currentLastParentNode.nodeName.match(/^figure$/gi)) {
          const figureNode = this.createFigureNode(childImage, childFigcaption)

          // Insert figure after the last parent node
          currentLastParentNode.parentNode.insertBefore(figureNode, currentLastParentNode.nextSibling)

          // Remove the original image node
          currentNode.remove()

          // If the last parent node has no child node, remove it
          if (!currentLastParentNode.childNodes.length) {
            currentLastParentNode.remove()
          }
        }

        // Set contenteditable to true for <img> inside <figure> because it's not enabled by default
        childImage.setAttribute('contenteditable', true)

        // Check image size and set width/height attributes
        handleCheckImageSize(childImage)

        if (_.isNil(childFigcaption)) {
          // Do nothing if <figcaption> tag does not exist
          return
        }

        /**
         *
         * FIGCAPTION
         *
         */
        logger.debug('TinyMCE events (NodeChange) - figure figcaption', event.parents, currentNode, childFigcaption)

        // Set contenteditable to true for <figcaption> inside <figure> because it's not enabled by default
        childFigcaption.setAttribute('contenteditable', true)

        // TODO: Fix core part of TinyMCE and send pull request to the GITHUB official repo
        if (_.isEqual(childFigcaption.innerHTML, 'Caption')) {
          // Default text of TinyMCE when creating a new figcaption
          childFigcaption.innerHTML = imageCaptionTextDefault
        }

        // TODO: Disallow headers and blockquotes within <figcaption>
      })

      // youtube の埋め込み時に rel=0 を付与して関連動画には同じチャンネル内の動画を表示する
      editor.on('NodeChange', event => {
        if (event.element.children.length === 0) {
          return
        }
        const childIframe = _.find(event.element.children[0].children, child =>
          _.isEqual(child.nodeName.toLowerCase(), 'iframe'),
        )

        const currentNode = event.element
        const currentNodeName = currentNode.nodeName.toLowerCase()

        if (!_.isEqual(currentNodeName, 'p') || _.isNil(childIframe)) {
          // iframe 以外の要素が変更されたケースでは何もしない
          return
        }

        const childIframeSrc = childIframe.getAttribute('src')
        const youtubeEmbedUrlPattern = /^(http:|https:|)\/\/www\.youtube\.com\/embed\/([^?]+)(\?.+)?/

        if (!youtubeEmbedUrlPattern.test(childIframeSrc)) {
          // youtube埋め込みタグ以外の要素が変更されたケースでは何もしない
          return
        }

        logger.debug(
          'TinyMCE events (NodeChange) - p span youtube iframe',
          event.element,
          event.parents,
          currentNodeName,
        )

        // 一度のみ行う
        if (_.isNil(childIframe.getAttribute('data-youtube-rel-checked'))) {
          if (!_.isNil(childIframeSrc) && childIframeSrc.indexOf('rel=') === -1) {
            childIframe.setAttribute('src', `${childIframeSrc}${childIframeSrc.indexOf('?') === -1 ? '?' : '&'}rel=0`)
          }
          childIframe.setAttribute('data-youtube-rel-checked', true)
        }
      })

      const replaceWithIframeEmbed = (targetNode, embedHtml) => {
        const parentNode = targetNode.parentNode

        if (parentNode.className !== 'trill-description mce-content-body') {
          parentNode.setAttribute('contenteditable', false)
        }

        const iframe = document.createElement('iframe')
        iframe.width = '100%'
        iframe.src = `javascript: '${embedHtml.replace(/'/g, '"')}'`

        // 読み込み完了後に iframe 内のコンテンツの高さを取得して iframe の高さを変更
        iframe.onload = () => {
          const iframeDoc = iframe.contentWindow.document
          const margin = 80
          const contentHeight = iframeDoc.body.scrollHeight + margin

          logger.debug(`iframe onload content height #${contentHeight} px`)

          iframeDoc.body.setAttribute('style', 'overflow: auto; height: 100%')

          // FIXME: iframe の高さを設定 (iframe の高さが fit せずスクロールが出てしまう...)
          iframe.height = contentHeight
        }

        iframe.setAttribute('style', 'border: none')

        // a タグと iframe を差し替える
        parentNode.replaceChild(iframe, targetNode)
      }

      const getScriptEmbed = scriptContain => {
        let scriptElement
        if (scriptContain.children.length === 0) {
          if (scriptContain.nodeName.toLowerCase() === 'script') {
            scriptElement = scriptContain
          }
        } else {
          scriptElement = _.find(scriptContain.children, child => _.isEqual(child.nodeName.toLowerCase(), 'script'))
        }
        // Embed code does not define `type` in script
        scriptElement.type = 'text/javascript'
        scriptElement.async = true
        scriptElement.defer = true
        return scriptElement
      }

      editor.on('NodeChange', event => {
        const instagramEmbed = document.querySelector('blockquote.instagram-media')
        if (!_.isNil(instagramEmbed)) {
          // Fetch blockquote & script tag, put it to iframe to preview on editor
          const scriptElement = getScriptEmbed(instagramEmbed.nextElementSibling)
          const embedHtml = instagramEmbed.outerHTML + scriptElement.outerHTML

          // Remove script element cause already convert to iframe
          scriptElement.remove()

          replaceWithIframeEmbed(instagramEmbed, embedHtml)
        }
      })

      editor.addButton('snsEmbedX', {
        icon: 'im-sns-embed-x',
        tooltip: 'X ツイートの埋め込み',
        onclick() {
          let selectedNode = editor.selection.getNode()

          if (selectedNode.nodeName.toLowerCase() !== 'a') {
            selectedNode = _.find(selectedNode.children, childNode => childNode.nodeName.toLowerCase() === 'a')
          }

          const linkUrl = selectedNode.href

          oembedApi
            .getEmbedHtml({
              articleId: this.props.articleId,
              url: linkUrl,
            })
            .then(response => {
              logger.debug('get embed html response', response)

              const embedHtml = _.get(response, 'data.html')

              replaceWithIframeEmbed(selectedNode, embedHtml)
            })
            .catch(error => {
              logger.error('get embed html error', error)
            })
        },
        onPostRender() {
          editor.on('NodeChange', event => {
            const isLink = event.element.nodeName.toLowerCase() === 'a'

            const linkUrl = event.element.href

            const isX = /htt(p|ps):\/\/(twitter|x)\.com/.test(linkUrl)

            this.disabled(!isLink || !isX)
          })
        },
      })

      const openEmbedSNSModal = () => {
        const documentHeight = document.documentElement.clientHeight
        const maxModalHeight = Math.floor(documentHeight / 100) * 100 - 100
        const modalHeight = Math.min(maxModalHeight, 600)
        const modalWidth = (modalHeight * 4) / 3
        const previewSectionHeight = modalHeight - 200

        let snsCodeValue = ''

        const embedSNSCodePreviewed = () => {
          try {
            const [iframeYoutubeCode] = snsCodeValue.match(YOUTUBE_IFRAME_EMBED_PATTERN) || []
            const [iframeFacebookCode] = snsCodeValue.match(FACEBOOK_IFRAME_EMBED_PATTERN) || []
            const iframeCode = iframeYoutubeCode || iframeFacebookCode

            if (!_.isEmpty(iframeCode)) {
              const [, iframeHeight] = iframeCode.match(/height=["']([^"']*)["']/)
              const styleAdded = `height: ${iframeHeight}px`
              const iframe = iframeCode.match(/style=["']([^"']*)["']/)
                ? iframeCode.replace(/style=["']([^"']*)["']/, (_match, p1) => `style="${p1}; ${styleAdded}"`)
                : iframeCode.replace(/<iframe/, `<iframe style="${styleAdded}"`)

              return iframe
            }

            const [blockquoteCode] = snsCodeValue.match(BLOCKQUOTE_EMBED_SNS_CODE_PATTERN) || []
            const [scriptCode] = snsCodeValue.match(SCRIPT_EMBED_SNS_CODE_PATTERN) || []

            if (!_.isEmpty(blockquoteCode) && !_.isEmpty(scriptCode)) {
              return `
                <iframe
                  width="100%"
                  src="javascript: '${escapeEmbedCode(`${blockquoteCode} ${scriptCode}`)}'"
                  style="border: none;"
                ></iframe>
              `
            }

            return null
          } catch (error) {
            return null
          }
        }

        const embedSNSCodeAdded = () => {
          try {
            const [iframeYoutubeCode] = snsCodeValue.match(YOUTUBE_IFRAME_EMBED_PATTERN) || []
            const [iframeFacebookCode] = snsCodeValue.match(FACEBOOK_IFRAME_EMBED_PATTERN) || []
            const iframeCode = iframeYoutubeCode || iframeFacebookCode

            if (!_.isEmpty(iframeCode)) {
              return `
                <p contenteditable="false">
                  ${iframeCode}
                </p>
              `
            }

            const [blockquoteCode] = snsCodeValue.match(BLOCKQUOTE_EMBED_SNS_CODE_PATTERN) || []
            const [scriptCode] = snsCodeValue.match(SCRIPT_EMBED_SNS_CODE_PATTERN) || []

            if (!_.isEmpty(blockquoteCode) && !_.isEmpty(scriptCode)) {
              return `
                <p contenteditable="false">
                  <iframe
                    width="100%"
                    src="javascript: '${escapeEmbedCode(`${blockquoteCode} ${scriptCode}`)}'"
                    style="border: none"
                  ></iframe>
                </p>
              `
            }

            return null
          } catch (error) {
            return null
          }
        }

        editor.windowManager.open({
          title: 'SNSコードを埋め込む',
          width: modalWidth,
          height: modalHeight,
          body: [
            {
              type: 'label',
              text: '埋め込みコードを入力',
              style: 'margin: 6px 0',
            },
            {
              type: 'textbox',
              name: 'snsCode',
              multiline: true,
              minHeight: 100,
              oninput(e) {
                snsCodeValue = e.target.value
              },
            },
            { type: 'label', text: 'プレビュー', style: 'margin: 6px 0' },
            {
              type: 'container',
              name: 'snsPreview',
              html: `
                <div id="sns-preview" style="border: 1px solid #ddd; height: ${previewSectionHeight}px; width: 100%; text-align: center; overflow: auto;">
                  <p style="display: flex;align-items: center;justify-content: center;height: 100%;text-align: center;margin: 0;word-wrap: break-word; white-space: normal;color: #00b5ad;">
                    SNSのプレビューはありません。
                  </p>
                </div>
              `,
            },
          ],
          buttons: [
            {
              text: 'キャンセル',
              onclick: () => {
                editor.windowManager.close()
              },
            },
            {
              text: 'プレビュー',
              style: 'background-color: #f0ad4e; color: white;',
              onclick: () => {
                const previewContainer = document.getElementById('sns-preview')
                const embedCode = embedSNSCodePreviewed()

                if (_.isEmpty(embedCode)) {
                  previewContainer.innerHTML = `
                    <p style="display: flex;align-items: center;justify-content: center;height: 100%;text-align: center;margin: 0;word-wrap: break-word; white-space: normal;color: #db2828;">
                      埋め込みコードの形式が無効です。<br />対応している埋め込みコードは、X、Facebook、Instagram、Threads、TikTok、Reddit、Youtubeのみです。
                    </p>
                  `
                } else {
                  previewContainer.innerHTML = embedCode
                }
              },
            },
            {
              text: '送信',
              subtype: 'primary',
              onclick: () => {
                const previewContainer = document.getElementById('sns-preview')
                const embedCode = embedSNSCodeAdded()

                if (_.isEmpty(embedCode)) {
                  previewContainer.innerHTML = `
                    <p style="display: flex;align-items: center;justify-content: center;height: 100%;text-align: center;margin: 0;color: #db2828;">
                      埋め込みコードの形式が無効です。<br />対応している埋め込みコードは、X、Facebook、Instagram、Threads、TikTok、Reddit、Youtubeのみです。
                    </p>
                  `
                } else {
                  editor.insertContent(embedSNSCodeAdded())
                  editor.windowManager.close()
                }
              },
            },
          ],
        })
      }

      editor.addButton('embedSNS', {
        icon: 'code',
        tooltip: 'SNSの埋め込み',
        onClick() {
          openEmbedSNSModal()
        },
      })

      const openHtmlEmbedDialogFor = node => {
        const selectionToolbar = document.querySelector('.mce-tinymce-inline.mce-floatpanel')
        if (selectionToolbar) {
          selectionToolbar.dataset.trillMceSelectionToolbarDisabled = true
        }

        const figure = node && node.closest ? node.closest('figure.trill-html-iframe-embed') : null
        this.setState({
          htmlEmbedDialog: { open: true, initial: parseEmbedFigure(figure) || {}, targetNode: figure },
        })
      }

      editor.addButton('htmlIframeEmbed', {
        icon: 'newdocument',
        tooltip: 'HTML埋め込み',
        onClick: () => openHtmlEmbedDialogFor(editor.selection.getNode()),
      })

      editor.on('dblclick', event => {
        const figure =
          event.target && event.target.closest ? event.target.closest('figure.trill-html-iframe-embed') : null
        if (!figure) return
        event.preventDefault()
        openHtmlEmbedDialogFor(figure)
      })

      const openArticleInternalLinkDialog = () => {
        let articleId
        editor.windowManager.open({
          title: '内部リンク挿入',
          classes: 'article-internal-link',
          width: 447,
          height: 210,
          body: [
            {
              type: 'label',
              for: 'article-id',
              text: '記事ID',
              classes: 'article-internal-link__search-lable',
            },
            {
              type: 'container',
              layout: 'flex',
              direction: 'row',
              align: 'center',
              spacing: 6,
              classes: 'article-internal-link__search-input-container',
              items: [
                {
                  type: 'textbox',
                  name: 'article-id',
                  width: 191,
                  classes: 'article-internal-link__search-input',
                  onKeyup(event) {
                    articleId = event.target.value
                  },
                },
              ],
            },
            {
              type: 'container',
              layout: 'flex',
              direction: 'row',
              align: 'center',
              spacing: 6,
              id: 'article-internal-link-search-result-container',
              classes: 'article-internal-link__search-result-container',
            },
          ],
          onSubmit(e) {
            const articleInSertContent = `
              <div class="article-internal-link" contenteditable="false" data-article-id="${articleId}" style="display: flex; align-items: center; margin: 20px 2px; padding: 12px; border: 2px solid #d9d9d9; border-radius: 16px; height: 84px; overflow: hidden; position: relative;">
                <a href="/articles/${articleId}" style=" position: absolute; top: 0; left: 0; width: 100%; height: 100%; color: transparent; overflow: hidden; -webkit-user-select: none; user-select: none; text-decoration: none !important;" class="article-internal-link-title">
                  記事の情報受け取るため、一度記事を保存してください
                </a>
                <div style="width: 60px; height: 60px;">
                  <img style="max-height: 100%; width: 100%; height: 100%;" src=""/>
                </div>
                <div class="article-internal-link-title" style="flex: 1; margin: 0 0 0 8px; font-size: 14px; line-height: 24px; max-height: 48px; overflow: hidden; text-overflow: ellipsis; white-space: normal; word-break: break-all; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2;">
                </div>
              </div>
              <p></p>
            `
            editor.insertContent(articleInSertContent)
          },
        })
      }

      editor.addMenuItem('downloadimage', {
        text: 'ダウンロード',
        context: 'image',
        disabled: true,
        onclick() {
          const selectedNode = editor.selection.getNode()

          if (selectedNode && selectedNode.nodeName === 'IMG') {
            const imageUrl = selectedNode.src

            if (imageUrl) {
              let fileName = imageUrl.split('/').pop()

              fetch(imageUrl, { cache: 'no-store' })
                .then(response => {
                  const contentType = response.headers.get('Content-Type') || 'image/png'

                  const extension = contentType.split('/')[1]
                  const knownExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg']

                  const currentExtension = fileName.split('.').pop()

                  if (!knownExtensions.includes(currentExtension)) {
                    fileName += `.${extension}`
                  }

                  return response.blob()
                })
                .then(blob => {
                  // Create a downloadable URL
                  const downloadUrl = window.URL.createObjectURL(blob)
                  const link = document.createElement('a')
                  link.href = downloadUrl
                  link.download = fileName
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                })
                .catch(err => {
                  logger.error('Error downloading image:', err)
                })
            } else {
              logger.error('Image URL not found')
            }
          } else {
            logger.error('Selected node is not an image')
          }
        },
        onPostRender() {
          editor.on('NodeChange', event => {
            const isImage = event.element.nodeName.toLowerCase() === 'img'
            this.disabled(!isImage)
          })
        },
      })

      editor.addButton('articleInternalLink', {
        icon: 'link',
        tooltip: 'Article internal Link',
        onClick() {
          openArticleInternalLinkDialog()
        },
        onpostrender(data) {
          editor.on('NodeChange', event => {
            const selectedNode = editor.selection.getNode()
            const isArticleInternalLink =
              selectedNode.nodeName.toLowerCase() === 'div' && /(article-internal-link)/.test(selectedNode.className)

            if (isArticleInternalLink) {
              editor.selection.setCursorLocation(selectedNode)
            }
          })
        },
      })

      editor.addButton('snsEmbedTikTok', {
        icon: 'im-sns-embed-tiktok',
        tooltip: 'TikTok 投稿の埋め込み',
        onclick() {
          let selectedNode = editor.selection.getNode()

          if (selectedNode.nodeName.toLowerCase() !== 'a') {
            selectedNode = _.find(selectedNode.children, childNode => childNode.nodeName.toLowerCase() === 'a')
          }

          const linkUrl = selectedNode.href

          oembedApi
            .getEmbedHtml({
              articleId: this.props.articleId,
              url: linkUrl,
            })
            .then(response => {
              logger.debug('get embed html response', response)

              const embedHtml = _.get(response, 'data.html')

              replaceWithIframeEmbed(selectedNode, embedHtml)
            })
            .catch(error => {
              logger.error('get embed html error', error)
            })
        },
        onpostrender() {
          editor.on('NodeChange', event => {
            const isLink = event.element.nodeName.toLowerCase() === 'a'

            const linkUrl = event.element.href

            const isTikTok = /htt(p|ps):\/\/w{3}\.tiktok/.test(linkUrl)

            this.disabled(!isLink || !isTikTok)
          })
        },
      })

      editor.addButton('snsEmbedReddit', {
        icon: 'im-sns-embed-reddit',
        tooltip: 'Reddit 投稿の埋め込み',
        onclick() {
          let selectedNode = editor.selection.getNode()

          if (selectedNode.nodeName.toLowerCase() !== 'a') {
            selectedNode = _.find(selectedNode.children, childNode => childNode.nodeName.toLowerCase() === 'a')
          }

          const linkUrl = selectedNode.href

          oembedApi
            .getEmbedHtml({
              articleId: this.props.articleId,
              url: linkUrl,
            })
            .then(response => {
              logger.debug('get embed html response', response)

              const embedHtml = _.get(response, 'data.html')

              replaceWithIframeEmbed(selectedNode, embedHtml)
            })
            .catch(error => {
              logger.error('get embed html error', error)
            })
        },
        onpostrender() {
          editor.on('NodeChange', event => {
            const isLink = event.element.nodeName.toLowerCase() === 'a'

            const linkUrl = event.element.href

            const isReddit = /htt(p|ps):\/\/www\.reddit\.com/.test(linkUrl)

            this.disabled(!isLink || !isReddit)
          })
        },
      })

      // 引用の引用元を設定するボタン
      editor.addButton('cite', {
        icon: 'im-blockquote-cite',
        tooltip: '引用元',
        onclick() {
          editor.execCommand('mceToggleFormat', false, 'cite')
        },
        onpostrender() {
          editor.on('NodeChange', event => {
            logger.debug('TinyMCE events (NodeChange) - cite button', event.element, event.parents)

            const isCite = event.element.nodeName.toLowerCase() === 'cite'
            const parentBlockquote = _.find(event.parents, parent => parent.nodeName.toLowerCase() === 'blockquote')

            // 引用ブロックの中でのみ引用元を設定できる
            if (isCite && _.isNil(parentBlockquote)) {
              editor.execCommand('mceToggleFormat', false, 'cite')
              return
            }

            // 引用ブロック内で引用元ではない部分を選択した場合
            if (!_.isNil(parentBlockquote) && !isCite) {
              const cites = _.filter(parentBlockquote.children, child => child.nodeName.toLowerCase() === 'cite')
              // すでに引用元が設定されていた場合
              if (_.size(cites) > 0) {
                this.active(false)
                this.disabled(true)
                return
              }
            }

            this.disabled(_.isNil(parentBlockquote))
            this.active(isCite)
          })
        },
      })

      const linkButtonAccentPatterns = [
        {
          class: 'button--accent-1',
          icon: 'im-link-button-accent-1',
          text: '#022458',
        },
        {
          class: 'button--accent-2',
          icon: 'im-link-button-accent-2',
          text: '#DA789F',
        },
        {
          class: 'button--accent-3',
          icon: 'im-link-button-accent-3',
          text: '#66A079',
        },
        {
          class: 'button--accent-4',
          icon: 'im-link-button-accent-4',
          text: '#96C7E6',
        },
        {
          class: 'button--accent-5',
          icon: 'im-link-button-accent-5',
          text: '#CA0B27',
        },
        {
          class: 'button--accent-6',
          icon: 'im-link-button-accent-6',
          text: '#AAAAAA',
        },
        {
          class: 'button--accent-7',
          icon: 'im-link-button-accent-7',
          text: '#FFCC1A',
        },
        {
          class: 'button--accent-8',
          icon: 'im-link-button-accent-8',
          text: '#6FBBBC',
        },
        {
          class: 'button--accent-9',
          icon: 'im-link-button-accent-9',
          text: '#8D784D',
        },
        {
          class: 'button--accent-10',
          icon: 'im-link-button-accent-10',
          text: '#C8358D',
        },
        {
          class: 'button--accent-11',
          icon: 'im-link-button-accent-11',
          text: '#3BC985',
        },
        {
          class: 'button--accent-12',
          icon: 'im-link-button-accent-12',
          text: '#FAB650',
        },
        {
          class: 'button--accent-13',
          icon: 'im-link-button-accent-13',
          text: '#C72424',
        },
      ]

      const linkButtonAccentClasses = _.map(linkButtonAccentPatterns, 'class')

      const linkButtonAccentMenu = []
      const linkButtonAccentMenuItems = []

      // リンクボタン（主にスポコンで使う）を設定するボタン
      editor.addButton('linkButton', {
        icon: 'im-link-button',
        tooltip: 'リンクボタン',
        onclick() {
          editor.execCommand('mceToggleFormat', false, 'blockButton')

          const selectedNode = editor.selection.getNode()

          if (!selectedNode.classList.contains('button')) {
            selectedNode.classList.remove(...linkButtonAccentClasses)
          }
        },
        onpostrender() {
          editor.on('NodeChange', event => {
            const isLink = event.element.nodeName.toLowerCase() === 'a'

            const isBlockButton =
              event.element.classList.contains('button') && event.element.classList.contains('button--block')

            if (isBlockButton && _.isNil(isLink)) {
              editor.execCommand('mceToggleFormat', false, 'blockButton')
              return
            }

            this.disabled(!isLink)
            this.active(isBlockButton)
          })
        },
      })

      // リンクボタンの色パターンを選択するメニューのアイテム
      _.each(linkButtonAccentPatterns, pattern => {
        linkButtonAccentMenu.push({
          icon: pattern.icon,
          text: pattern.text,
          onclick() {
            if (!_.includes(linkButtonAccentMenuItems, this)) {
              linkButtonAccentMenuItems.push(this)
            }

            const selectedNode = editor.selection.getNode()

            if (selectedNode.classList.contains(pattern.class)) {
              selectedNode.classList.remove(pattern.class)
              this.active(false)
            } else {
              selectedNode.classList.remove(...linkButtonAccentClasses)
              _.each(linkButtonAccentMenuItems, menuItem => {
                menuItem.active(false)
              })

              selectedNode.classList.add(pattern.class)
              this.active(true)
            }
          },
          onpostrender() {
            editor.on('NodeChange', event => {
              const isSelected = event.element.classList.contains(pattern.class)
              this.active(isSelected)
            })
          },
        })
      })

      // リンクボタンの色パターンを選択するメニュー
      editor.addButton('linkButtonAccent', {
        type: 'menubutton',
        icon: 'im-link-button-accent',
        tooltip: '色パターン',
        menu: linkButtonAccentMenu,
        onpostrender() {
          editor.on('NodeChange', event => {
            const isLink = event.element.nodeName.toLowerCase() === 'a'

            const isBlockButton =
              event.element.classList.contains('button') && event.element.classList.contains('button--block')

            this.disabled(!isLink || !isBlockButton)
          })
        },
      })

      // Add the device-dependent character check button
      editor.addButton('checkDeviceChars', {
        icon: 'warning',
        tooltip: '環境依存文字・非対応特殊文字チェック',
        onclick: () => {
          const text = editor.getContent({ format: 'text' })
          const htmlContent = editor.getContent()

          // Create array of all device-dependent characters
          const deviceDepChars = Object.values(DEVICE_DEPENDENT_CHARS)
            .map(string => string.split(''))
            .flat()

          // Check for device-dependent characters (highlight only)
          const deviceDepRegex = new RegExp(`(${deviceDepChars.join('|')})`, 'gmi')
          const deviceDepMatches = text.match(deviceDepRegex)

          // Check for special spaces (warning + auto-removal)
          const escapedSpecialSpaces = SPECIAL_SPACES.split('')
            .map(char => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
            .join('|')
          const specialSpacesRegex = new RegExp(`(${escapedSpecialSpaces})`, 'g')

          const toast = {
            isShow: true,
            type: TOAST_TYPES.SUCCESS,
            message: '環境依存文字・非対応特殊文字が見つかりませんでした。',
          }

          let hasIssues = false
          const messageDetails = []

          // Handle special spaces (warning + auto-removal) FIRST
          let finalHtmlContent = htmlContent
          let totalSpecialSpacesRemoved = 0

          // Remove Unicode special spaces from HTML content
          finalHtmlContent = htmlContent.replace(specialSpacesRegex, match => {
            totalSpecialSpacesRemoved++
            return ''
          })

          // Remove HTML entities for special spaces
          HTML_ENTITY_SPACE_PATTERNS.forEach(pattern => {
            finalHtmlContent = finalHtmlContent.replace(pattern, match => {
              totalSpecialSpacesRemoved++
              return ''
            })
          })

          if (totalSpecialSpacesRemoved > 0) {
            editor.setContent(finalHtmlContent)
            messageDetails.push(`特殊スペース${totalSpecialSpacesRemoved}個が見つかりました。自動削除します。`)
            hasIssues = true

            this.handleChange(finalHtmlContent, this.editorError)
          }

          // Handle device-dependent characters (highlight only) AFTER content update
          if (!_.isEmpty(deviceDepMatches)) {
            // Use setTimeout to ensure the DOM is updated after setContent
            setTimeout(() => {
              highlightWords(
                document.querySelector('.ArticleDescriptionEditor .trill-description'),
                deviceDepChars,
                'device-dependent-char',
              )
            }, 100)
            messageDetails.push(`環境依存文字が${deviceDepMatches.length}個見つかりました。`)
            hasIssues = true
          }

          if (hasIssues) {
            toast.type = TOAST_TYPES.WARNING
            toast.message = `${messageDetails.join('')}`
          }

          this.setState({ toast }, () => {
            setTimeout(() => {
              toast.isShow = false
              this.setState({ toast })
            }, 5000)
          })
        },
      })
    }
    this.config.init_instance_callback = this.handleInit
  }

  componentDidMount() {
    window.addEventListener('message', this.handleEmbedResize)

    const tinyMceScriptUrl = `${process.env.REACT_APP_TRILL_ASSETS_URL}/tinymce/js/tinymce/tinymce.min.js`

    let isTinyMceScriptLoaded = false

    _.each(document.getElementsByTagName('script'), script => {
      if (script.src === tinyMceScriptUrl) {
        isTinyMceScriptLoaded = true
      }
    })

    if (isTinyMceScriptLoaded) {
      this.setState({
        isTinyMceScriptLoaded: true,
      })
    } else {
      const tinyMceScript = document.createElement('script')
      tinyMceScript.setAttribute('src', tinyMceScriptUrl)

      document.body.appendChild(tinyMceScript)

      tinyMceScript.onload = () => {
        this.setState({
          isTinyMceScriptLoaded: true,
        })
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleEmbedResize)
  }

  componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.description, nextProps.description)) {
      // 初回のみ呼ばれる
      this.initialDescription = nextProps.description
      this.description = nextProps.description

      const editorContent = this.editor ? this.editor.getContent() : ''
      if (!_.isEqual(this.description, editorContent)) {
        this.editor.setContent(this.description)
        this.handleUpdateEmbedStyle()
        this.injectHtmlIframeEmbedPreviews()
      }
    }
  }

  handleUpdateEmbedStyle = () => {
    document.querySelectorAll('.trill-description iframe').forEach(iframe => {
      if (!_.isNil(iframe.getAttribute('height'))) {
        return
      }

      iframe.onload = () => {
        try {
          const frameDocumentBody = iframe.contentWindow.document.body
          const childIframe = frameDocumentBody.querySelector('iframe')

          if (childIframe) {
            childIframe.setAttribute('style', 'width: 100%')
          }

          setTimeout(() => {
            if (frameDocumentBody) {
              iframe.height = frameDocumentBody.scrollHeight === 150 ? 400 : frameDocumentBody.scrollHeight
            }
          })
        } catch (error) {
          iframe.height = 400
        }
      }
    })
  }

  injectHtmlIframeEmbedPreviews = () => {
    if (!this.editor) return

    const body = this.editor.getBody()
    if (!body) return

    const figures = body.querySelectorAll('figure.trill-html-iframe-embed')
    figures.forEach(figure => {
      if (figure.querySelector('iframe[data-mce-bogus]')) return

      const script = figure.querySelector('script[type$="text/trill-html-iframe-embed"]')
      if (!script) return

      const rawInner = (script.textContent || '').replace(/<\\\/script>/gi, '</script>')
      const sandbox = safeSandbox(figure.getAttribute('data-sandbox'))
      const userWidth = parseInt(figure.getAttribute('data-width'), 10) || 0
      const userHeight = parseInt(figure.getAttribute('data-height'), 10) || 0

      TrillDescription.TrillHtmlIframeEmbed.parse(rawInner, { validateAll: true })
        .then(embed => {
          if (figure.querySelector('iframe[data-mce-bogus]')) return

          const previewInner = embed.sanitizedHtml()
          const previewSrcdoc = `<!doctype html>
            <html>
              <head>
                <meta charset="utf-8">
                <style>body { margin: 0; padding: 8px; font-family: sans-serif; }</style>
              </head>
              <body>
                ${previewInner}
                <script>${HTML_EMBED_AUTO_RESIZE_SCRIPT}</script>
              </body>
            </html>
          `

          const iframe = figure.ownerDocument.createElement('iframe')
          iframe.setAttribute('data-mce-bogus', 'all')
          iframe.setAttribute('srcdoc', previewSrcdoc)
          iframe.setAttribute('sandbox', sandbox)
          iframe.setAttribute('loading', 'lazy')

          const dims = resolveEmbedDimensions({
            width: userWidth,
            height: userHeight,
            maxWidth: HTML_EMBED_ALLOW_LIST.maxWidth,
            maxHeight: HTML_EMBED_ALLOW_LIST.maxHeight,
          })
          if (dims.mode === 'aspect') {
            iframe.dataset.htmlEmbedFixedAspect = '1'
            iframe.style.width = `${dims.width}px`
            iframe.style.maxWidth = '100%'
            iframe.style.aspectRatio = `${dims.width} / ${dims.height}`
            iframe.style.height = 'auto'
          }

          figure.insertBefore(iframe, script)
        })
        .catch(() => {
          if (!this.editor) return

          this.editor.notificationManager.open({
            text: 'HTMLの埋め込みのプレビューの生成に失敗しました。内容を確認してください。',
            type: 'error',
          })
        })
    })
  }

  /**
   * constructor の後に呼ばれる editor の初期化ハンドラ
   */
  handleInit = editor => {
    this.editor = editor
    this.editor.undoManager.clear()
    this.editor.setContent(this.description)
    this.handleUpdateEmbedStyle()
    this.injectHtmlIframeEmbedPreviews()

    _.invoke(this.props, 'onInit', { ...this.props, editor })
  }

  handleEmbedResize = event => {
    if (!event.data || event.data.type !== 'trill:resize') return

    const rawHeight = parseInt(event.data.height, 10)
    if (!(rawHeight > 0)) return

    const height = Math.min(rawHeight, HTML_EMBED_RESIZE_MAX_PX)
    if (!this.editor) return

    const body = this.editor.getBody()
    if (!body) return

    body.querySelectorAll('figure.trill-html-iframe-embed > iframe[data-mce-bogus]').forEach(iframe => {
      if (iframe.dataset.htmlEmbedFixedAspect === '1') return
      if (iframe.contentWindow === event.source) {
        iframe.style.height = `${height}px`
      }
    })
  }

  handleChange = (description, error) => {
    _.invoke(this.props, 'onChange', { ...this.props, description, error })
  }

  handleHtmlEmbedDialogClose = () => {
    this.setState({ htmlEmbedDialog: { open: false, initial: {}, targetNode: null } })
  }

  handleHtmlEmbedDialogSubmit = figureHtml => {
    const { targetNode } = this.state.htmlEmbedDialog
    if (targetNode && targetNode.parentNode) {
      const tempDiv = targetNode.ownerDocument.createElement('div')
      tempDiv.innerHTML = figureHtml
      const newFigure = tempDiv.firstChild
      if (newFigure) {
        targetNode.parentNode.replaceChild(newFigure, targetNode)
      }
    } else {
      this.editor.focus()
      this.editor.insertContent(figureHtml)
    }

    this.injectHtmlIframeEmbedPreviews()
  }

  handleByteLengthWarning = display => {
    if (this.isWarningByteLength !== display) {
      this.isWarningByteLength = display
      _.invoke(this.props, 'fireByteLengthWarning', display)
    }
  }

  /**
   * Recursively searches for a parent node with the specified name up to a certain depth.
   *
   * @param {Node} node - The starting node for the search.
   * @param {string} targetNodeName - The name of the node to search for.
   * @param {number} depth - The maximum depth to search (default is 2).
   * @returns {Node|null} - The found node, or null if not found within the given depth.
   */
  findParentNode = (node, targetNodeName, depth = 2) => {
    if (_.isNil(node) || depth < 0) {
      return null
    }

    if (_.isEqual(node.nodeName.toLowerCase(), targetNodeName.toLowerCase())) {
      return node
    }

    return this.findParentNode(node.parentNode, targetNodeName, depth - 1)
  }

  /**
   * Recursively searches for a child node with the specified name up to a certain depth.
   *
   * @param {Node} node - The starting node for the search.
   * @param {string} targetNodeName - The name of the node to search for.
   * @param {number} depth - The maximum depth to search (default is 2).
   * @returns {Node|null} - The found node, or null if not found within the given depth.
   */
  findChildNode = (node, targetNodeName, depth = 2) => {
    if (_.isNil(node) || depth < 0) {
      return null
    }

    if (_.isEqual(node.nodeName.toLowerCase(), targetNodeName.toLowerCase())) {
      return node
    }

    return _.reduce(
      node.childNodes,
      (foundNode, childNode) => foundNode || this.findChildNode(childNode, targetNodeName, depth - 1),
      null,
    )
  }

  /**
   * Recursively finds the index of a target child node within a parent node's child nodes.
   *
   * @param {Node} node - The parent node to search within.
   * @param {Node} targetNode - The target child node to find.
   * @returns {number} - The index of the target child node, or -1 if not found.
   */
  findChildNodeIndex = (node, targetNode) => {
    if (_.isNil(node) || _.isNil(targetNode)) {
      return -1
    }

    return _.findIndex(
      node.childNodes,
      childNode => childNode.isEqualNode(targetNode) || this.findChildNodeIndex(childNode, targetNode) >= 0,
    )
  }

  /**
   * Creates a <figure> element containing the provided <img> and <figcaption> nodes.
   *
   * @param {Node} imgNode - The <img> node to include in the figure.
   * @param {Node} figcaptionNode - The <figcaption> node to include in the figure.
   * @returns {HTMLElement} - The created <figure> element.
   */
  createFigureNode = (imgNode, figcaptionNode) => {
    const figure = document.createElement('figure')
    figure.className = 'image'
    figure.setAttribute('contenteditable', false)
    figure.innerHTML = `
      ${_.isNil(imgNode) ? '' : imgNode.outerHTML}
      <figcaption contenteditable="true">
        ${_.isNil(figcaptionNode) ? imageCaptionTextDefault : figcaptionNode.innerHTML}
      </figcaption>
    `
    return figure
  }

  /**
   * Moves extra child nodes of the current node (excluding specified nodes) into a new <p> element.
   *
   * @param {Node} currentNode - The node from which to move extra nodes.
   * @param {Node[]} excludeNodes - An array of nodes to exclude from moving.
   * @returns {HTMLElement|null} - The created <p> element with extra nodes, or null if no extra nodes exist.
   */
  moveExtraNodesToParagraph = (currentNode, excludeNodes) => {
    const pNode = document.createElement('p')
    const extraNodes = _.filter(currentNode.childNodes, node => {
      const isExcluded = excludeNodes.includes(node)
      const isChildNodeExcluded = _.some(node.childNodes, childNode =>
        _.some(excludeNodes, excludedNode => childNode.isEqualNode(excludedNode)),
      )

      return !isExcluded && !isChildNodeExcluded
    })

    extraNodes.forEach(node => pNode.appendChild(node))

    return pNode.childNodes.length ? pNode : null
  }

  /**
   * Normalizes a <figure> node to ensure it contains an <img> and optionally a <figcaption>.
   * If the structure is incorrect, it restructures the node and moves extra nodes to a new <p>.
   *
   * @param {Node} currentNode - The <figure> node to normalize.
   * @returns {HTMLElement|null} - The normalized <figure> node, or null if the node is invalid.
   */
  normalizeFigureNode = currentNode => {
    if (_.isNil(currentNode) || !_.isEqual(currentNode.nodeName.toLowerCase(), 'figure')) {
      return null
    }

    const childImage = this.findChildNode(currentNode, 'img')
    if (!childImage) {
      currentNode.remove()
      return null
    }

    const childFigcaption = this.findChildNode(currentNode, 'figcaption')
    const isStructuredCorrectly = !_.isNil(childFigcaption) && currentNode.children.length === 2
    if (isStructuredCorrectly) {
      return currentNode
    }

    return this.normalizeImageInsertion(currentNode, childImage, childFigcaption)
  }

  /**
   * Normalizes an image insertion by wrapping the image and optional figcaption in a <figure>,
   * and moving any extra nodes to a new <p> element.
   *
   * @param {Node} node - The node containing the image and optional figcaption.
   * @param {Node} childImage - The <img> node to include in the figure.
   * @param {Node} childFigcaption - The <figcaption> node to include in the figure (optional).
   * @returns {HTMLElement} - The created <figure> element.
   */
  normalizeImageInsertion = (node, childImage, childFigcaption) => {
    const parentNode = node.parentNode
    const figureNode = this.createFigureNode(childImage, childFigcaption)
    const paragraphNode = this.moveExtraNodesToParagraph(node, [childImage, childFigcaption])
    const childImageIndex = this.findChildNodeIndex(node, childImage)

    parentNode.replaceChild(figureNode, node)
    if (_.isNil(paragraphNode)) {
      return figureNode
    }

    if (!childImageIndex) {
      parentNode.insertBefore(paragraphNode, figureNode)
    } else if (_.isNil(figureNode.nextSibling)) {
      parentNode.appendChild(paragraphNode)
    } else {
      parentNode.insertBefore(paragraphNode, figureNode.nextSibling)
    }

    return figureNode
  }

  render() {
    const { isBusy, isTinyMceScriptLoaded, toast, htmlEmbedDialog } = this.state

    return (
      <div className="ArticleDescriptionEditor">
        <Dimmer active={isBusy || !isTinyMceScriptLoaded} inverted>
          <Loader>読み込み中</Loader>
        </Dimmer>

        {!_.isEmpty(toast) && (
          <Transition visible={toast.isShow} animation="fade down" duration={500}>
            <Message
              className="toast"
              positive={_.isEqual(toast.type, TOAST_TYPES.SUCCESS)}
              negative={_.isEqual(toast.type, TOAST_TYPES.ERROR)}
              warning={_.isEqual(toast.type, TOAST_TYPES.WARNING)}
              icon={TOAST_ICONS[toast.type]}
              header={toast.message}
            />
          </Transition>
        )}

        {isTinyMceScriptLoaded && <TinyMce className="trill-description" config={this.config} />}

        <HtmlEmbedDialog
          open={htmlEmbedDialog.open}
          initialHtml={htmlEmbedDialog.initial.html}
          initialWidth={htmlEmbedDialog.initial.width}
          initialHeight={htmlEmbedDialog.initial.height}
          initialSandbox={htmlEmbedDialog.initial.sandbox}
          onClose={this.handleHtmlEmbedDialogClose}
          onSubmit={this.handleHtmlEmbedDialogSubmit}
        />
      </div>
    )
  }
}

ArticleDescriptionEditor.propTypes = propTypes
ArticleDescriptionEditor.defaultProps = defaultProps

export default ArticleDescriptionEditor
